import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { User, MapPin, Phone, Envelope, IdentificationCard, PencilSimple } from '@phosphor-icons/react';

export function CustomerSection({ customer, onEdit }) {
    if (!customer) {
        return (
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-base">
                            <User className="w-5 h-5 text-primary" />
                            Dati Cliente
                        </CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground text-sm">Nessun cliente associato</p>
                </CardContent>
            </Card>
        );
    }

    const isAzienda = customer.tipo === 'azienda';
    const displayName = isAzienda 
        ? customer.ragione_sociale 
        : `${customer.nome || ''} ${customer.cognome || ''}`.trim();

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <User className="w-5 h-5 text-primary" />
                        Dati Cliente
                        <Badge variant="outline" className="ml-2">
                            {isAzienda ? 'Azienda' : 'Privato'}
                        </Badge>
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={onEdit} data-testid="edit-customer-btn">
                        <PencilSimple className="w-4 h-4" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Nome/Ragione Sociale */}
                <div>
                    <h4 className="font-semibold text-lg">{displayName || 'N/D'}</h4>
                    {isAzienda && customer.forma_giuridica && (
                        <p className="text-sm text-muted-foreground capitalize">{customer.forma_giuridica}</p>
                    )}
                </div>

                {/* Codice Fiscale / P.IVA */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-muted-foreground">Codice Fiscale</p>
                        <p className="font-mono text-sm">{customer.codice_fiscale || '-'}</p>
                    </div>
                    {isAzienda && (
                        <div>
                            <p className="text-xs text-muted-foreground">Partita IVA</p>
                            <p className="font-mono text-sm">{customer.partita_iva || '-'}</p>
                        </div>
                    )}
                </div>

                {/* Persona fisica - dati nascita */}
                {!isAzienda && (customer.data_nascita || customer.luogo_nascita) && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs text-muted-foreground">Data di Nascita</p>
                            <p className="text-sm">{customer.data_nascita || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">Luogo di Nascita</p>
                            <p className="text-sm">{customer.luogo_nascita || '-'}</p>
                        </div>
                    </div>
                )}

                {/* Documento */}
                {!isAzienda && customer.documento_tipo && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <IdentificationCard className="w-3 h-3" /> Documento
                        </p>
                        <p className="text-sm">
                            {customer.documento_tipo?.replace('_', ' ')} - {customer.documento_numero || 'N/D'}
                            {customer.documento_scadenza && (
                                <span className="text-muted-foreground ml-2">(scad. {customer.documento_scadenza})</span>
                            )}
                        </p>
                    </div>
                )}

                {/* Azienda - Referente */}
                {isAzienda && customer.referente_nome && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Referente</p>
                        <p className="text-sm font-medium">{customer.referente_nome}</p>
                        {customer.referente_ruolo && (
                            <p className="text-xs text-muted-foreground">{customer.referente_ruolo}</p>
                        )}
                        {customer.referente_telefono && (
                            <p className="text-xs">{customer.referente_telefono}</p>
                        )}
                    </div>
                )}

                {/* Indirizzo */}
                {(customer.indirizzo || customer.citta) && (
                    <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Indirizzo
                        </p>
                        <p className="text-sm">
                            {customer.indirizzo}
                            {customer.citta && `, ${customer.citta}`}
                            {customer.cap && ` ${customer.cap}`}
                            {customer.provincia && ` (${customer.provincia})`}
                        </p>
                    </div>
                )}

                {/* Contatti */}
                <div className="flex flex-wrap gap-4">
                    {customer.telefono && (
                        <div className="flex items-center gap-1 text-sm">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            {customer.telefono}
                        </div>
                    )}
                    {customer.cellulare && (
                        <div className="flex items-center gap-1 text-sm">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            {customer.cellulare}
                        </div>
                    )}
                    {customer.email && (
                        <div className="flex items-center gap-1 text-sm">
                            <Envelope className="w-4 h-4 text-muted-foreground" />
                            {customer.email}
                        </div>
                    )}
                    {customer.pec && (
                        <div className="flex items-center gap-1 text-sm">
                            <Envelope className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">(PEC)</span> {customer.pec}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
